/**
 * VoiceConfirmModal — Phase 5 Voice Assistant
 *
 * Shown after VoiceButton receives a classification from POST /api/ai/voice-intent.
 * Displays the transcript, classified intent, and extracted entity so the healer
 * can confirm or cancel BEFORE any write action executes.
 *
 * CRITICAL CONSTRAINT: No write action (DB update, API call) may execute until the
 * healer presses "Confirm" in this modal. VoiceButton never auto-executes actions.
 *
 * Props:
 *   intentData   {Object|null}  — { intent, entity, confidence, suggestedAction, transcript }
 *                                 Pass null or undefined to hide the modal
 *   onConfirm    {Function}     — called with intentData when healer confirms
 *   onCancel     {Function}     — called when healer cancels or closes
 */

import React from 'react';

// Human-readable labels for each intent code
const INTENT_LABELS = {
  book_session:    'Book Session',
  check_status:    'Check Healing Status',
  cancel_request:  'Cancel Healing Request',
  log_session:     'Log Session',
  chakra_scan:     'Chakra Scan',
  generate_invoice:'Generate Invoice',
  approve_payout:  'Approve Payout',
  assign_healer:   'Assign Healer',
  get_summary:     'Get Summary',
  general_query:   'General Query'
};

export default function VoiceConfirmModal({ intentData, onConfirm, onCancel }) {
  if (!intentData) return null;

  const { intent, entity, confidence, suggestedAction, transcript } = intentData;
  const intentLabel     = INTENT_LABELS[intent] || intent;
  const confidencePct   = Math.round((confidence || 0) * 100);
  const lowConfidence   = confidence < 0.5;

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Confirm voice action">
      <div style={styles.modal}>

        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerIcon}>🎤</span>
          <h2 style={styles.title}>Confirm Voice Action</h2>
        </div>

        {/* Transcript */}
        <div style={styles.section}>
          <span style={styles.label}>You said:</span>
          <p style={styles.transcript}>"{transcript}"</p>
        </div>

        {/* Classification */}
        <div style={styles.classificationRow}>
          <div style={styles.classificationItem}>
            <span style={styles.label}>Intent</span>
            <span style={styles.intentBadge}>{intentLabel}</span>
          </div>
          {entity && (
            <div style={styles.classificationItem}>
              <span style={styles.label}>Entity</span>
              <span style={styles.entityBadge}>{entity}</span>
            </div>
          )}
          <div style={styles.classificationItem}>
            <span style={styles.label}>Confidence</span>
            <span style={{ ...styles.confidenceBadge, ...(lowConfidence ? styles.lowConfidence : {}) }}>
              {confidencePct}%
            </span>
          </div>
        </div>

        {/* Suggested action */}
        {suggestedAction && (
          <div style={styles.actionHint}>
            <span style={styles.actionHintIcon}>→</span>
            <span>{suggestedAction}</span>
          </div>
        )}

        {/* Low-confidence warning */}
        {lowConfidence && (
          <div style={styles.warning}>
            Low confidence — please re-record if this doesn't match what you said.
          </div>
        )}

        {/* Buttons — minimum 44×44px touch targets (CLAUDE.md MOBILE) */}
        <div style={styles.buttonRow}>
          <button
            type="button"
            onClick={onCancel}
            style={{ ...styles.button, ...styles.cancelButton }}
            aria-label="Cancel voice action"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(intentData)}
            style={{ ...styles.button, ...styles.confirmButton }}
            aria-label="Confirm voice action"
          >
            Confirm
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position:        'fixed',
    inset:           0,
    background:      'rgba(0,0,0,0.5)',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    zIndex:          1000,
    padding:         '16px'
  },
  modal: {
    background:   '#ffffff',
    borderRadius: '12px',
    padding:      '24px',
    maxWidth:     '440px',
    width:        '100%',
    boxShadow:    '0 20px 40px rgba(0,0,0,0.15)'
  },
  header: {
    display:      'flex',
    alignItems:   'center',
    gap:          '10px',
    marginBottom: '20px'
  },
  headerIcon: {
    fontSize: '24px'
  },
  title: {
    margin:     0,
    fontSize:   '18px',
    fontWeight: '600',
    color:      '#111827'
  },
  section: {
    marginBottom: '16px'
  },
  label: {
    display:     'block',
    fontSize:    '12px',
    fontWeight:  '600',
    color:       '#6b7280',
    marginBottom:'4px',
    textTransform:'uppercase',
    letterSpacing:'0.05em'
  },
  transcript: {
    margin:       0,
    fontSize:     '15px',
    color:        '#374151',
    fontStyle:    'italic',
    lineHeight:   '1.5',
    background:   '#f9fafb',
    padding:      '10px 12px',
    borderRadius: '6px',
    borderLeft:   '3px solid #6b7280'
  },
  classificationRow: {
    display:      'flex',
    gap:          '12px',
    flexWrap:     'wrap',
    marginBottom: '16px'
  },
  classificationItem: {
    display:        'flex',
    flexDirection:  'column',
    gap:            '4px'
  },
  intentBadge: {
    padding:      '4px 10px',
    background:   '#dbeafe',
    color:        '#1d4ed8',
    borderRadius: '20px',
    fontSize:     '13px',
    fontWeight:   '600'
  },
  entityBadge: {
    padding:      '4px 10px',
    background:   '#d1fae5',
    color:        '#065f46',
    borderRadius: '20px',
    fontSize:     '13px',
    fontWeight:   '600'
  },
  confidenceBadge: {
    padding:      '4px 10px',
    background:   '#f0fdf4',
    color:        '#15803d',
    borderRadius: '20px',
    fontSize:     '13px',
    fontWeight:   '600'
  },
  lowConfidence: {
    background: '#fef9c3',
    color:      '#854d0e'
  },
  actionHint: {
    display:      'flex',
    alignItems:   'center',
    gap:          '8px',
    padding:      '10px 12px',
    background:   '#f0f9ff',
    borderRadius: '6px',
    fontSize:     '14px',
    color:        '#0369a1',
    marginBottom: '16px'
  },
  actionHintIcon: {
    fontWeight:  '700',
    flexShrink:  0
  },
  warning: {
    padding:      '10px 12px',
    background:   '#fef9c3',
    borderRadius: '6px',
    fontSize:     '13px',
    color:        '#854d0e',
    marginBottom: '16px'
  },
  buttonRow: {
    display:        'flex',
    gap:            '12px',
    justifyContent: 'flex-end'
  },
  button: {
    padding:      '11px 24px',
    minHeight:    '44px',      // CLAUDE.md MOBILE: touch target minimum 44×44px
    minWidth:     '88px',
    borderRadius: '8px',
    border:       'none',
    fontSize:     '15px',
    fontWeight:   '600',
    cursor:       'pointer',
    transition:   'opacity 0.15s ease'
  },
  cancelButton: {
    background: '#f3f4f6',
    color:      '#374151'
  },
  confirmButton: {
    background: '#2563eb',
    color:      '#ffffff'
  }
};
